import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "./Reveal";

const TABS = [
  { id: "saas", label: "SaaS Dashboard", testid: "showcase-tab-saas" },
  { id: "mobile", label: "Mobile Marketplace", testid: "showcase-tab-mobile" },
  { id: "copilot", label: "Real-Time AI Copilot", testid: "showcase-tab-copilot" },
];

const BUILDS = {
  saas: [
    "$ emergent build \"saas analytics dashboard\"",
    "agent: drafting 12 routes + role model",
    "db: provisioning mongodb cluster … ok",
    "auth: jwt + refresh rotation … ok",
    "ui: react 19 + tailwind charts … ok",
    "deploy: edge runtime live in 03:58",
  ],
  mobile: [
    "$ emergent build \"marketplace mobile app\"",
    "agent: mapping expo screens + native nav",
    "db: listings, orders, chat threads … ok",
    "payments: stripe connect hooks … ok",
    "ui: ios + android parity build … ok",
    "deploy: qr preview live in 04:21",
  ],
  copilot: [
    "$ emergent build \"ai support copilot\"",
    "agent: wiring llm tool-calling loop",
    "db: vector index + ticket store … ok",
    "realtime: websocket stream … ok",
    "ui: chat console + escalation view … ok",
    "deploy: edge runtime live in 03:44",
  ],
};

const SaasMock = () => (
  <div className="card-soft rounded-2xl p-4">
    <div className="flex items-center gap-2 border-b border-[#e8e8e4] pb-3">
      <span className="h-2 w-2 rounded-full bg-[#ff5f57]" />
      <span className="h-2 w-2 rounded-full bg-[#febc2e]" />
      <span className="ml-3 rounded-full border border-[#e8e8e4] bg-[#f8f8f6] px-3 py-0.5 font-mono text-[9px] text-slate-500">
        analytics.emergent.site
      </span>
    </div>
    <div className="mt-4 flex gap-4">
      <div className="hidden w-14 flex-col gap-2 sm:flex">
        {[...Array(5)].map((_, i) => (
          <span key={i} className={`h-6 rounded-md ${i === 0 ? "bg-[#eff4ff]" : "bg-[#f1f1ee]"}`} />
        ))}
      </div>
      <div className="flex-1">
        <div className="grid grid-cols-3 gap-3">
          {["MRR", "Users", "Churn"].map((s) => (
            <div key={s} className="rounded-xl border border-[#e8e8e4] bg-[#f8f8f6] p-3">
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">{s}</p>
              <p className="mt-1.5 h-3.5 w-3/4 rounded bg-[#e2e2de]" />
            </div>
          ))}
        </div>
        <div className="mt-3 flex h-28 items-end gap-2 rounded-xl border border-[#e8e8e4] bg-[#f8f8f6] p-3">
          {[35, 55, 40, 70, 52, 80, 64, 92].map((h, i) => (
            <motion.span
              key={i}
              initial={{ height: 0 }}
              animate={{ height: `${h}%` }}
              transition={{ delay: 0.15 + i * 0.07, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className={`flex-1 rounded-sm ${i === 7 ? "bg-[#2563eb]" : "bg-[#c7d5ff]"}`}
            />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const MobileMock = () => (
  <div className="mx-auto w-52 rounded-[2rem] border-4 border-[#0b1120] bg-white p-3 shadow-xl">
    <div className="mx-auto mb-3 h-4 w-20 rounded-full bg-[#0b1120]" />
    <div className="h-24 rounded-xl bg-[#2563eb]" />
    <div className="mt-3 space-y-2">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="flex items-center gap-2.5 rounded-lg border border-[#e8e8e4] bg-[#f8f8f6] p-2.5">
          <span className="h-8 w-8 rounded-full bg-[#e2e2de]" />
          <div className="flex-1 space-y-1.5">
            <span className="block h-2 w-3/4 rounded bg-[#e2e2de]" />
            <span className="block h-2 w-1/2 rounded bg-[#eeeeea]" />
          </div>
        </div>
      ))}
    </div>
    <div className="mt-3 flex justify-around rounded-full border border-[#e8e8e4] py-2">
      {[...Array(4)].map((_, i) => (
        <span key={i} className={`h-4 w-4 rounded ${i === 0 ? "bg-[#2563eb]" : "bg-[#e2e2de]"}`} />
      ))}
    </div>
  </div>
);

const CopilotMock = () => (
  <div className="card-soft rounded-2xl p-4">
    <div className="space-y-3">
      <div className="ml-auto w-3/4 rounded-2xl rounded-br-sm bg-[#2563eb] p-3">
        <span className="block h-2 w-full rounded bg-white/50" />
        <span className="mt-1.5 block h-2 w-2/3 rounded bg-white/50" />
      </div>
      <div className="w-5/6 rounded-2xl rounded-bl-sm border border-[#e8e8e4] bg-[#f8f8f6] p-3">
        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#2563eb]">agent · tool_call: search_docs</p>
        <div className="mt-2 space-y-1.5 rounded-lg bg-[#0b1120] p-2.5 font-mono text-[9px] text-slate-400">
          <p>{"→ tickets.similar(query, k=5)"}</p>
          <p className="text-[#34d399]">{"✓ 5 matches · drafting reply"}</p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 pl-1">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            animate={{ opacity: [0.2, 1, 0.2] }}
            transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
            className="h-1.5 w-1.5 rounded-full bg-[#2563eb]"
          />
        ))}
      </div>
    </div>
  </div>
);

export const Showcase = () => {
  const [tab, setTab] = useState("saas");
  const [shown, setShown] = useState([]);

  useEffect(() => {
    setShown([]);
    let i = 0;
    const iv = setInterval(() => {
      i += 1;
      setShown(BUILDS[tab].slice(0, i));
      if (i >= BUILDS[tab].length) clearInterval(iv);
    }, 420);
    return () => clearInterval(iv);
  }, [tab]);

  return (
    <section id="live-engine" className="scroll-mt-24 border-t border-[#e8e8e4] bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#2563eb]">02 // The Live Engine</p>
          <h2 className="font-display mt-6 max-w-2xl text-2xl font-semibold leading-[1.15] text-[#0b0f1a] sm:text-3xl lg:text-4xl">
            One prompt. Two platforms. <span className="text-[#2563eb]">Zero waiting.</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-wrap gap-2.5">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={t.testid}
                onClick={() => setTab(t.id)}
                className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-all duration-300 ${
                  tab === t.id
                    ? "border-[#2563eb] bg-[#2563eb] text-white shadow-[0_8px_20px_-8px_rgba(37,99,235,0.6)]"
                    : "border-[#e8e8e4] bg-[#f8f8f6] text-slate-500 hover:border-[#2563eb]/40 hover:text-[#0b0f1a]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-10 grid gap-8 lg:grid-cols-2">
          <Reveal delay={0.15} className="h-full">
            <div className="console-shadow flex h-full flex-col rounded-3xl bg-[#0b1120]">
              <div className="flex items-center justify-between border-b border-white/10 px-5 py-3.5">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  build.log — streaming
                </span>
                <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#34d399]">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#34d399]" /> live
                </span>
              </div>
              <div className="flex-1 space-y-3 px-5 py-6 font-mono text-xs leading-relaxed sm:text-sm">
                <AnimatePresence mode="popLayout">
                  {shown.map((l, i) => (
                    <motion.p
                      key={`${tab}-${i}`}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.35 }}
                      className={i === 0 ? "text-slate-100" : l.endsWith("ok") || l.includes("live") ? "text-[#34d399]" : "text-slate-500"}
                    >
                      {l}
                    </motion.p>
                  ))}
                </AnimatePresence>
                {shown.length < BUILDS[tab].length && (
                  <span className="caret inline-block h-4 w-2 bg-[#60a5fa]" />
                )}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.25} className="h-full">
            <div
              data-testid="showcase-device-preview"
              className="relative flex h-full items-center justify-center overflow-hidden rounded-3xl border border-[#e8e8e4] bg-[#f8f8f6] p-6 sm:p-10"
            >
              <div className="pointer-events-none absolute inset-0 hero-grid" />
              <span className="absolute left-3 top-3 font-mono text-slate-300">+</span>
              <span className="absolute right-3 top-3 font-mono text-slate-300">+</span>
              <span className="absolute bottom-3 left-3 font-mono text-slate-300">+</span>
              <span className="absolute bottom-3 right-3 font-mono text-slate-300">+</span>
              <div className="relative w-full max-w-md">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={tab}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {tab === "saas" && <SaasMock />}
                    {tab === "mobile" && <MobileMock />}
                    {tab === "copilot" && <CopilotMock />}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
